"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { ChevronDown, User, Menu, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useAuth } from "@/lib/auth-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ProfileDialog from "@/components/profile/ProfileDialog";

// Pages are mirrored 1:1 under /en, so the other-locale URL can be derived
// from the current path without a per-page lookup table.
function otherLocaleHref(pathname: string, locale: "hr" | "en") {
  if (locale === "en") {
    const stripped = pathname.replace(/^\/en/, "");
    return stripped === "" ? "/" : stripped;
  }
  return pathname === "/" ? "/en/" : `/en${pathname}`;
}

function LanguageSwitcher({ className }: { className?: string }) {
  const t = useTranslations("Navbar");
  const locale = useLocale() as "hr" | "en";
  const pathname = usePathname();
  const href = otherLocaleHref(pathname, locale);

  return (
    <Link
      href={href}
      aria-label={locale === "hr" ? t("switchToEnAria") : t("switchToHrAria")}
      className={
        className ??
        "inline-flex h-10 items-center rounded-full border border-[#e4e4e7] bg-white px-4 text-sm font-medium text-[#18181b] shadow-sm transition-colors hover:bg-[#fafafa]"
      }
    >
      {locale === "hr" ? "EN" : "HR"}
    </Link>
  );
}

export default function Navbar() {
  const { is_authenticated, user, logout } = useAuth();
  const t = useTranslations("Navbar");
  const locale = useLocale() as "hr" | "en";
  const localePrefix = locale === "en" ? "/en" : "";
  const homeHref = locale === "en" ? "/en/" : "/";
  const equipHref = `${localePrefix}/opremi-teretanu`;
  const membershipsHref = locale === "en" ? "/en/#clanarine" : "/#clanarine";
  const router = useRouter();
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    router.push(homeHref);
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b border-[#e4e4e7] bg-white">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">

          {/* Logo */}
          <Link href={homeHref} className="flex items-center select-none">
            <Image src="/img/logo.svg" alt="Saiyan Gym" width={77} height={29} priority />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-6 md:flex">
            <Link
              href={equipHref}
              className="text-sm font-medium text-[#18181b] transition-opacity hover:opacity-70"
            >
              {t("equipCta")}
            </Link>

            <LanguageSwitcher className="text-sm font-medium text-[#18181b] transition-opacity hover:opacity-70" />

            {!is_authenticated ? (
              <div className="flex items-center gap-3">
                {/* Prijavi se — outlined pill */}
                <Link
                  href="/login"
                  className="inline-flex h-10 items-center rounded-full border border-[#e4e4e7] bg-white px-5 text-sm font-medium text-[#18181b] shadow-sm transition-colors hover:bg-[#fafafa]"
                >
                  {t("login")}
                </Link>

                {/* Učlani se — yellow filled pill */}
                <Link
                  href="/register"
                  className="inline-flex h-10 items-center rounded-full bg-brand px-5 text-sm font-medium text-brand-foreground shadow-sm transition-opacity hover:opacity-90"
                >
                  {t("join")}
                </Link>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex h-10 items-center gap-2 rounded-full border border-[#e4e4e7] bg-white px-4 text-sm font-medium text-[#18181b] transition-colors hover:bg-[#fafafa]">
                    <User className="h-4 w-4 shrink-0 text-[#18181b]" />
                    <span className="max-w-[160px] truncate">{user?.email}</span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-[#71717a]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-1">
                  <div className="px-3 py-2 text-sm font-bold text-[#18181b] truncate">{user?.email}</div>
                  <DropdownMenuItem onSelect={() => setProfileOpen(true)} className="cursor-pointer">
                    {t("myProfile")}
                  </DropdownMenuItem>
                  {user?.role === "admin" && (
                    <DropdownMenuItem onSelect={() => router.push("/admin")} className="cursor-pointer">
                      {t("adminPanel")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={handleLogout} className="cursor-pointer">
                    {t("logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          {/* Mobile actions */}
          <div className="flex items-center gap-3 md:hidden">
            {!is_authenticated && (
              <Link
                href="/register"
                className="inline-flex h-10 items-center rounded-full bg-brand px-5 text-sm font-medium text-brand-foreground shadow-sm transition-opacity hover:opacity-90"
              >
                {t("join")}
              </Link>
            )}

            <button
              type="button"
              className="flex h-10 w-10 touch-manipulation items-center justify-center rounded-full border border-[#e4e4e7]"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={t("toggleMenuAria")}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5 text-[#18181b]" />
              ) : (
                <Menu className="h-5 w-5 text-[#18181b]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu — full-screen overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 flex flex-col bg-white md:hidden">
            <div className="flex h-[72px] shrink-0 items-center justify-between px-6">
              <Link href={homeHref} className="flex items-center select-none" onClick={() => setMobileMenuOpen(false)}>
                <Image src="/img/logo.svg" alt="Saiyan Gym" width={77} height={29} priority />
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label={t("closeMenuAria")}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e4e4e7]"
              >
                <X className="h-5 w-5 text-[#18181b]" />
              </button>
            </div>

            <nav className="flex flex-1 flex-col items-center justify-center gap-8">
              <Link
                href={membershipsHref}
                className="text-lg font-medium text-[#18181b]"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("memberships")}
              </Link>
              <Link
                href={equipHref}
                className="text-lg font-medium text-[#18181b]"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("equipCta")}
              </Link>

              <LanguageSwitcher className="text-lg font-medium text-[#18181b]" />

              {is_authenticated && (
                <>
                  <p className="text-lg font-medium text-[#18181b]">{user?.email}</p>
                  <button
                    className="text-lg font-medium text-[#18181b]"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setProfileOpen(true);
                    }}
                  >
                    {t("myProfile")}
                  </button>
                  {user?.role === "admin" && (
                    <button
                      className="text-lg font-medium text-[#18181b]"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        router.push("/admin");
                      }}
                    >
                      {t("adminPanel")}
                    </button>
                  )}
                  <button
                    className="text-lg font-medium text-destructive"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    {t("logout")}
                  </button>
                </>
              )}
            </nav>

            {!is_authenticated && (
              <div className="flex shrink-0 flex-col gap-3 px-6 pb-8">
                <Link
                  href="/login"
                  className="inline-flex h-12 items-center justify-center rounded-full border border-[#e4e4e7] bg-white px-5 text-sm font-medium text-[#18181b]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("login")}
                </Link>
                <Link
                  href="/register"
                  className="inline-flex h-12 items-center justify-center rounded-full bg-brand px-5 text-sm font-medium text-brand-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("join")}
                </Link>
              </div>
            )}
          </div>
        )}
      </header>

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}
